import { useState, useEffect, useMemo } from "react";
import {
  Typography, Grid2 as Grid, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { Loading } from "../../components/loading/Loading";
import ClientCard from "../../components/cards/ClientCard";
import { getUsers, changeStatus, editFiscales, sendRejectionEmail } from "../../actions/users";

import { connect } from "react-redux";

const ListRequest = ({
  getUsers, changeStatus, editFiscales, sendRejectionEmail,
  users, loading: loadingUsers,
  user
}) => {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [editingUser, setEditingUser] = useState({ id: "", email: "", razon: "", rfc: "" });

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  const clients = useMemo(() => {
    if (!users) return [];
    return users.filter(user =>
      user.datos_empresas &&
      user.datos_empresas.length > 0 &&
      user.datos_empresas[0].rfc && // Must have RFC
      (user.datos_empresas[0].status === 0 || user.datos_empresas[0].status === undefined ||
        user.datos_empresas[0].status === null) // Pending or undefined status
    ).map(user => ({
      _id: user._id,
      name: user.name,
      razon_social: user.datos_empresas[0].razon_social || "N/A",
      email: user.email,
      rfc: user.datos_empresas[0].rfc || "N/A",
      status: "pendiente"
    }));
  }, [users]);

  const handleAccept = async id => {
    const userToAccept = users.find(u => u._id === id);
    if (!userToAccept) return;

    const rfcToAccept = userToAccept.datos_empresas?.[0]?.rfc;

    if (rfcToAccept) {
      const duplicate = users.find(u =>
        u._id !== id &&
        u.datos_empresas?.[0]?.rfc === rfcToAccept &&
        u.datos_empresas?.[0]?.status === 1
      );

      if (duplicate) {
        setDuplicateInfo({
          name: `${duplicate.name}`,
          rfc: rfcToAccept
        });
        setOpenModal(true);
        return;
      }
    }

    await changeStatus(1, id);
    return;
  };

  const handleDelete = async id => {
    const userToReject = users.find(u => u._id === id);
    if (userToReject) {
      const emailData = {
        email: userToReject.email,
        nombre: userToReject.name
      };
      await sendRejectionEmail(emailData);
    }
    await changeStatus(2, id);
    return;
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setDuplicateInfo(null);
  };

  const handleEdit = (id) => {
    const userToEdit = users.find(u => u._id === id);
    if (userToEdit) {
      setEditingUser({
        id: userToEdit._id,
        email: userToEdit.email,
        razon: userToEdit.datos_empresas?.[0]?.razon_social || "",
        rfc: userToEdit.datos_empresas?.[0]?.rfc || "",
        // Add other fields if necessary, preserving existing structure if editFiscales expects it
        ...userToEdit.datos_empresas?.[0] // Spread existing data to avoid losing other fields
      });
      setOpenModalEdit(true);
    }
  };

  const handleChangeEdit = (e) => {
    setEditingUser({
      ...editingUser,
      [e.target.name]: e.target.value
    });
  };

  const handleSaveEdit = async () => {
    // Construct the object expected by editFiscales
    // editFiscales expects (formData, id)
    // formData usually matches the structure in FormHome/MiPerfil
    // We need to make sure we send what's needed.
    // Based on MiPerfil, it sends newRegister which has flat fields like razon, rfc, etc.
    // Let's check editFiscales implementation or usage in MiPerfil.
    // In MiPerfil: editFiscales(newRegister, user._id);
    // newRegister has: razon, rfc, regimen, pais, estado, etc.
    // We should send at least the modified fields.

    await editFiscales(editingUser, editingUser.id);
    setOpenModalEdit(false);
    getUsers();
  };

  if (loadingUsers) {
    return <Loading />
  }

  return (
    <Grid container mt={{ xs: 10, md: 12 }} spacing={2}>
      <Grid size={12}>
        <Typography mt={3} variant="h5" color="primary" sx={{ fontWeight: "bold" }}>
          Solicitudes de clientes
        </Typography>
        <Typography
          variant="body2"
          color="#737791"
          sx={{ marginBottom: 1 }}
        >
          Información completa y gestión
        </Typography>
      </Grid>
      <Grid size={12}>
        <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
          {clients.length > 0 ? (
            clients.map((item, index) => (
              <Grid
                key={`statistic${index}`}
                size={12}
              >
                <ClientCard
                  userClient={item}
                  handleEdit={user?.tipo_usuario === true ? handleEdit : undefined}
                  handleDelete={() => handleDelete(item._id)}
                  handleAccept={() => handleAccept(item._id)}
                />
              </Grid>
            ))
          ) : (
            <Grid size={12}>
              <Typography variant="body1" align="center">
                No hay solicitudes pendientes.
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"RFC Duplicado"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#737791" }} id="alert-dialog-description">
            El RFC <strong>{duplicateInfo?.rfc}</strong> ya se encuentra registrado y aprobado en el usuario <strong>{duplicateInfo?.name}</strong>.
            <br /><br />
            No es posible aprobar esta solicitud con un RFC duplicado.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} autoFocus>
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openModalEdit} onClose={() => setOpenModalEdit(false)}>
        <DialogTitle>Editar Solicitud</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Edita la información del solicitante. El correo electrónico no se puede modificar.
          </DialogContentText>
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={editingUser.email}
            disabled
          />
          <TextField
            margin="dense"
            label="Razón Social"
            name="razon"
            fullWidth
            value={editingUser.razon}
            onChange={handleChangeEdit}
          />
          <TextField
            margin="dense"
            label="RFC"
            name="rfc"
            fullWidth
            value={editingUser.rfc}
            onChange={handleChangeEdit}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModalEdit(false)}>Cancelar</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">Guardar</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

const mapStateToProps = state => ({
  users: state.users.users,
  loading: state.users.loading,
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  getUsers,
  changeStatus,
  editFiscales,
  sendRejectionEmail
})(ListRequest);
